import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { Button, Input, Form } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';
import { useDidUpdate, useToggle } from '../../lib/hooks';
import { isMobile } from '../../utils/env';
import { withPopup } from '../../lib/popup';
import { useForm } from '../../hooks';

import styles from './CompanyManageExpiredEditPopup.module.scss';
import moment from 'moment';

const CompanyManageExpiredEditPopup = React.memo(({ defaultValue, onUpdate, onClose }) => {
  const [data, handleFieldChange, setData] = useForm(() => {
    const date = defaultValue || new Date().setHours(0, 0, 0, 0);

    return {
      date: moment(date).format('YYYY-MM-DD'),
      time: moment(date).format('HH:mm'),
    };
  });

  const [selectTimeFieldState, selectTimeField] = useToggle();

  const dateField = useRef(null);
  const timeField = useRef(null);

  const nullableDate = useMemo(() => {
    const date = moment(data.date, 'YYYY-MM-DD').toDate();

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  }, [data.date]);

  const handleDatePickerChange = useCallback(
    (date) => {
      setData((prevData) => ({
        ...prevData,
        date: moment(date).format('YYYY-MM-DD'),
      }));
      selectTimeField();
    },
    [setData, selectTimeField],
  );

  const handleSubmit = useCallback(() => {
    if (!nullableDate) {
      dateField.current.select();
      return;
    }
    const value = new Date(`${data.date} ${data.time}`);
    if (Number.isNaN(value.getTime())) {
      timeField.current.select();
      return;
    }

    if (!defaultValue || value.getTime() !== defaultValue.getTime()) {
      onUpdate(value.toISOString());
    }

    onClose();
  }, [defaultValue, onUpdate, onClose, data, nullableDate]);

  useEffect(() => {
    if (!isMobile) {
      dateField.current.select();
    }
  }, []);

  useDidUpdate(() => {
    timeField.current.select();
  }, [selectTimeFieldState]);

  return (
    <>
      <Popup.Header>企业修改有效期</Popup.Header>
      <Popup.Content>
        <Form onSubmit={handleSubmit}>
          <div className={styles.fieldWrapper}>
            <div className={styles.fieldBox}>
              <div className={styles.text}>日期</div>
              <Input ref={dateField} name="date" value={data.date} onChange={handleFieldChange} />
            </div>
            <div className={styles.fieldBox}>
              <div className={styles.text}>时间</div>
              <Input ref={timeField} name="time" value={data.time} onChange={handleFieldChange} />
            </div>
          </div>
          <DatePicker
            inline
            disabledKeyboardNavigation
            selected={nullableDate}
            onChange={handleDatePickerChange}
          />
          <Button type="submit" positive className={styles.saveButton} content="保存" />
        </Form>
      </Popup.Content>
    </>
  );
});

CompanyManageExpiredEditPopup.propTypes = {
  defaultValue: PropTypes.instanceOf(Date),
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CompanyManageExpiredEditPopup.defaultProps = {
  defaultValue: undefined,
};

export default withPopup(CompanyManageExpiredEditPopup);
